import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

// Formly validators (for use in app.config.ts)
export function isValidCardNumber(control: AbstractControl): ValidationErrors | null {
  if (!control.value) {
    return null;
  }

  const value = control.value.replace(/\D/g, '');

  // Luhn Algorithm
  let sum = 0;
  let shouldDouble = false;

  for (let i = value.length - 1; i >= 0; i--) {
    let digit = parseInt(value.charAt(i), 10);
    if (shouldDouble && (digit *= 2) > 9) {
      digit -= 9;
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }

  return sum % 10 === 0 ? null : { isValidCardNumber: true };
}

export function isValidExpiryDate(control: AbstractControl): ValidationErrors | null {
  if (!control.value) {
    return null;
  }

  const value = control.value.replace(/\D/g, '');
  if (value.length < 6) {
    return { isValidExpiryDate: true };
  }

  const month = parseInt(value.substring(0, 2), 10);
  const year = parseInt(value.substring(2, 6), 10);

  if (month < 1 || month > 12) {
    return { isValidExpiryDate: true };
  }

  const now = new Date();
  const expiry = new Date(year, month - 1);

  if (expiry < now) {
    return { isValidExpiryDate: true };
  }

  return null;
}

export function isValidInput(control: AbstractControl): ValidationErrors | null {
  if (!control.value) {
    return null;
  }

  // Basic alphanumeric with spaces and common punctuation
  const valid = /^[a-zA-Z0-9\s\-.,']+$/.test(control.value);
  return valid ? null : { isValidInput: true };
}

export function isDigit(control: AbstractControl): ValidationErrors | null {
  if (!control.value) {
    return null;
  }

  const valid = /^\d+$/.test(control.value);
  return valid ? null : { isDigit: true };
}

export class CustomValidator {
  static patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const valid = regex.test(control.value);
      return valid ? null : error;
    };
  }

  static validateDebitCard(error: ValidationErrors): ValidatorFn {
    const acceptedCreditCards: Record<string, RegExp> = {
      visa: /^4[0-9]{12}(?:[0-9]{3})?$/,
      mastercard: /^5[1-5][0-9]{14}$|^2(?:2(?:2[1-9]|[3-9][0-9])|[3-6][0-9][0-9]|7(?:[01][0-9]|20))[0-9]{12}$/,
      amex: /^3[47][0-9]{13}$/,
      discover:
        /^65[4-9][0-9]{13}|64[4-9][0-9]{13}|6011[0-9]{12}|(622(?:12[6-9]|1[3-9][0-9]|[2-8][0-9][0-9]|9[01][0-9]|92[0-5])[0-9]{10})$/,
      diners_club: /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/,
      jcb: /^(?:2131|1800|35[0-9]{3})[0-9]{11}$/,
    };

    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      try {
        const value = control.value.replace(/\D/g, '');
        let sum = 0;
        let shouldDouble = false;

        for (let i = value.length - 1; i >= 0; i--) {
          let digit = parseInt(value.charAt(i), 10);

          if (shouldDouble) {
            digit *= 2;
            if (digit > 9) {
              digit -= 9;
            }
          }

          sum += digit;
          shouldDouble = !shouldDouble;
        }

        const valid = sum % 10 === 0;
        let accepted = false;

        Object.keys(acceptedCreditCards).forEach((key) => {
          const regex = acceptedCreditCards[key];
          if (regex.test(value)) {
            accepted = true;
          }
        });

        return valid && accepted ? null : error;
      } catch {
        return null;
      }
    };
  }

  static validateCardType(error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const firstDigit = control.value.charAt(0);
      const valid = ['4', '5', '6'].includes(firstDigit);

      return valid ? null : error;
    };
  }

  static validateluhnAlgorithm(error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      if (/[^0-9-\s]+/.test(control.value)) {
        return { isValidCardNumber: { message: 'Invalid input entered.' } };
      }

      // Luhn Algorithm
      let nCheck = 0;
      let bEven = false;
      let value = control.value;

      value = value.replace(/\D/g, '');

      for (let n = value.length - 1; n >= 0; n--) {
        const cDigit = value.charAt(n);
        let nDigit = parseInt(cDigit, 10);

        if (bEven && (nDigit *= 2) > 9) {
          nDigit -= 9;
        }

        nCheck += nDigit;
        bEven = !bEven;
      }

      return nCheck % 10 === 0 ? null : error;
    };
  }

  static validateMonthFormat(error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const month = control.value.substring(0, 2);
      const valid = +month >= 1 && +month <= 12;

      return valid ? null : error;
    };
  }

  static validateDate(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const currYear = new Date().getFullYear();
      const currMonth = new Date().getMonth();
      const currDate = new Date(currYear, currMonth);

      const currDecade = currYear.toString().substring(0, 2);
      const month = control.value.substring(0, 2);
      const year = currDecade + control.value.substring(control.value.length - 2, control.value.length);
      const providedDate = new Date(+year, +month - 1);

      if (providedDate < currDate) {
        return { cardExpired: true };
      }

      if (providedDate.getTime() === currDate.getTime()) {
        return { cardExpiringSoon: true };
      }

      return null;
    };
  }
}
